import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge
} from 'reactflow';
import 'reactflow/dist/style.css';
import TeamNode from '../../../components/resellers/org/TeamNode';
import styles from './OrgPage.module.css';

const API_BASE = 'https://kindred-cloudflare.shepherdn.workers.dev';
const nodeTypes = {
  teamNode: TeamNode,
};

const ORG_ID = 'master_resellers/devco/orgs/engineering.json';

function OrgPage() {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [formData, setFormData] = useState({ name: '', role: '' });
  const isDragging = useRef(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/org?id=${ORG_ID}`)
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`GET failed: ${res.status} — ${text}`);
        }
        return res.json();
      })
      .then((data) => {
        setNodes(data.nodes);
        setEdges(data.edges);
      })
      .catch((err) => {
        console.error('❌ Org fetch error:', err);
      });
  }, []);

  const saveGraph = async (updatedNodes, updatedEdges) => {
    try {
      const res = await fetch(`${API_BASE}/api/org?id=${ORG_ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes: updatedNodes, edges: updatedEdges }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`PUT failed: ${res.status} — ${text}`);
      }
      console.log('✅ Graph saved');
    } catch (err) {
      console.error('❌ Failed to save graph:', err);
    }
  };

  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    []
  );

  const onEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    []
  );

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const updated = addEdge(params, eds);
        saveGraph(nodes, updated);
        return updated;
      });
    },
    [nodes]
  );

  const handleNodeDragStart = () => {
    isDragging.current = true;
  };

  const handleNodeDragStop = () => {
    isDragging.current = false;
    saveGraph(nodes, edges);
  };

  const handleAddMember = async () => {
    const newNode = {
      type: 'teamNode',
      position: { x: 200 + nodes.length * 50, y: 200 },
      data: {
        name: `New Member`,
        role: 'Contributor',
        avatarUrl: '/assets/images/AvatarDefault.png',
      },
    };

    try {
      const res = await fetch(`${API_BASE}/api/org?id=${ORG_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newNode),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`POST failed: ${res.status} — ${text}`);
      }
      const { node } = await res.json();
      const updatedNodes = [...nodes, node];
      setNodes(updatedNodes);
      saveGraph(updatedNodes, edges);
    } catch (err) {
      console.error('❌ Add member failed:', err);
    }
  };

  const handleUpdateNode = async () => {
    const updatePayload = {
      id: selectedNodeId,
      data: formData,
    };

    try {
      const res = await fetch(`${API_BASE}/api/org?id=${ORG_ID}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`PATCH failed: ${res.status} — ${text}`);
      }
      const { nodes: updatedNodes } = await res.json();
      setNodes(updatedNodes);
      setShowModal(false);
      saveGraph(updatedNodes, edges);
    } catch (err) {
      console.error('❌ Update member failed:', err);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <button onClick={handleAddMember}>Add member</button>
        <button>Edit roles</button>
        <button className={styles.reportButton}>Read Report</button>
        <button>Reorder structure</button>
      </div>

      <div style={{ flex: 1, padding: '2rem' }}>
        <nav className={styles.navbar}>
          <span className={styles.navItem}>Home</span>
          <span>Teams</span>
          <span>Reports</span>
          <div className={styles.profileButton}><button>Profile</button></div>
        </nav>

        <div className={styles.chartWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onNodeDragStart={handleNodeDragStart}
            onNodeDragStop={handleNodeDragStop}
            onNodeClick={(_, node) => {
              setSelectedNodeId(node.id);
              setFormData({ name: node.data.name, role: node.data.role });
              setShowModal(true);
            }}
          >
            <Background gap={16} />
            <Controls />
          </ReactFlow>
        </div>
      </div>

      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Edit Member</h3>
            <label>Name</label>
            <input
              name="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <label>Role</label>
            <input
              name="role"
              type="text"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            />
            <button onClick={handleUpdateNode}>Update</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrgPage;
